import type { PatternFormFieldItem, PatternFormFieldItemTag } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable, RecordKey } from '@ricoh/ria.client.core/types/core/data.type';

import type {
  Pattern,
  PatternBatch,
  PatternConstant,
  PatternDocument,
  PatternGenerate,
  PatternGlobal,
  PatternOutput,
  PatternParameter,
} from '~/common/model/api/batch-type/pattern';
import type { DataTypeNameGeneral } from '~/common/model/api/common';

import {
  PATTERN_VALUE_SYSTEM_VALUES_BATCH,
  PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT,
  PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT_FILE,
  PATTERN_VALUE_SYSTEM_VALUES_GENERATE,
  TRANSFORMATION_TEXT,
} from '~/common/constants/feature/settings/batch-type-library';

import type { PatternAccessScope } from './_common.type';

import { createPatternBatch, createPatternBatchFormField } from './_batch';
import { createPatternConstant, createPatternConstantFormField } from './_constant';
import { createPatternDocument, createPatternDocumentFormField } from './_document';
import { createPatternGenerate, createPatternGenerateFormField } from './_generate';
import { createPatternGlobal, createPatternGlobalFormField } from './_global';
import { createPatternOutput, createPatternOutputFormField } from './_output';
import { createPatternParameter, createPatternParameterFormField } from './_parameter';

/** Create Pattern. */
export const createPattern = {
  fromConstant: (type: DataTypeNameGeneral, value: unknown): Pattern => createPatternConstant.constant(type, value),

  fromParameter: (value: string): Pattern => createPatternParameter.parameter(value),

  fromOutput: (value: string): Pattern => createPatternOutput.output(value),

  fromSystemValue: <TTabKey extends RecordKey>(
    { value }: PatternFormFieldItemTag<TTabKey>,
    scope: PatternAccessScope,
    transformationText: boolean,
  ): Nullable<Pattern> => {
    if (PATTERN_VALUE_SYSTEM_VALUES_GENERATE.includes(value)) {
      switch (value) {
        case 'date':
        case 'time':
        case 'datetime': {
          return createPatternGenerate.now(value, transformationText);
        }

        case 'guid': {
          return createPatternGenerate.guid();
        }

        case 'random': {
          return createPatternGenerate.random(8);
        }

        default: {
          return undefined;
        }
      }
    }

    if (PATTERN_VALUE_SYSTEM_VALUES_BATCH.includes(value)) {
      return createPatternBatch.batchPath(value, scope, transformationText);
    }

    if (PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT.includes(value) || PATTERN_VALUE_SYSTEM_VALUES_DOCUMENT_FILE.includes(value)) {
      return createPatternDocument.documentPath(value, transformationText);
    }

    return undefined;
  },

  fromGlobalVariable: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, transformationText: boolean): Pattern => {
    const pattern = createPatternGlobal.globalVariable(value.toString());
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromGlobalBatchType: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, transformationText: boolean): Pattern => {
    const pattern = createPatternGlobal.globalBatchType(value.toString());
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromBatchField: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, scope: PatternAccessScope, transformationText: boolean): Pattern => {
    const pattern = createPatternBatch.batchField(value.toString(), scope);
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromBatchVariable: <TTabKey extends RecordKey>(
    { value }: PatternFormFieldItemTag<TTabKey>,
    scope: PatternAccessScope,
    transformationText: boolean,
  ): Pattern => {
    const pattern = createPatternBatch.batchVariable(value.toString(), scope);
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromBusinessRule: <TTabKey extends RecordKey>(
    { value }: PatternFormFieldItemTag<TTabKey>,
    scope: PatternAccessScope,
    transformationText: boolean,
  ): Pattern => {
    const pattern = createPatternBatch.batchBusinessRule(value.toString(), scope);
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromDocumentField: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, transformationText: boolean): Pattern => {
    const pattern = createPatternDocument.documentField(value.toString());
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromDocumentVariable: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, transformationText: boolean): Pattern => {
    const pattern = createPatternDocument.documentVariable(value.toString());
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },

  fromDocumentBusinessRule: <TTabKey extends RecordKey>({ value }: PatternFormFieldItemTag<TTabKey>, transformationText: boolean): Pattern => {
    const pattern = createPatternDocument.documentBusinessRule(value.toString());
    pattern.transformations = transformationText ? [TRANSFORMATION_TEXT] : undefined;

    return pattern;
  },
};

/** Create FormFieldItem. */
export const createPatternFormFieldItem = {
  fromPatternConstant: (pattern: PatternConstant): PatternFormFieldItem => createPatternConstantFormField.constant(pattern),

  fromPatternParameter: (pattern: PatternParameter): PatternFormFieldItem => createPatternParameterFormField.parameter('parameter', pattern),

  fromPatternOutput: (pattern: PatternOutput): PatternFormFieldItem => createPatternOutputFormField.output('output', pattern),

  fromPatternGlobal: (pattern: PatternGlobal): Nullable<PatternFormFieldItem> => createPatternGlobalFormField.globalVariable('globalVariable', pattern),

  fromPatternGlobalBatchType: (pattern: PatternGlobal): Nullable<PatternFormFieldItem> => createPatternGlobalFormField.globalBatchType('systemValue', pattern),

  fromPatternGenerate: (pattern: PatternGenerate, transformationText: boolean): Nullable<PatternFormFieldItem> => {
    switch (pattern.$is) {
      case 'now': {
        return createPatternGenerateFormField.now('systemValue', pattern.type, transformationText);
      }

      case 'guid':
      case 'random': {
        return createPatternGenerateFormField.default('systemValue', pattern.$is);
      }

      default: {
        return undefined;
      }
    }
  },

  fromPatternBatch: (pattern: PatternBatch): Nullable<PatternFormFieldItem> => {
    const creators = [
      () => createPatternBatchFormField.batchPath('systemValue', pattern),
      () => createPatternBatchFormField.batchField('batchField', pattern),
      () => createPatternBatchFormField.batchVariable('batchVariable', pattern),
      () => createPatternBatchFormField.batchBusinessRule('batchBusinessRule', pattern),
    ];

    let index = 0;
    let item: Nullable<PatternFormFieldItem>;
    while (!item && index < creators.length) {
      const create = creators[index];
      if (!create) {
        break;
      }

      item = create();
      index += 1;
    }

    return item;
  },

  fromPatternDocument: (pattern: PatternDocument): Nullable<PatternFormFieldItem> => {
    const creators = [
      () => createPatternBatchFormField.batchPath('systemValue', pattern),
      () => createPatternBatchFormField.batchField('batchField', pattern),
      () => createPatternBatchFormField.batchVariable('batchVariable', pattern),
      () => createPatternBatchFormField.batchBusinessRule('batchBusinessRule', pattern),

      () => createPatternDocumentFormField.documentPath('systemValue', pattern),
      () => createPatternDocumentFormField.documentField('documentField', pattern),
      () => createPatternDocumentFormField.documentVariable('documentVariable', pattern),
      () => createPatternDocumentFormField.documentBusinessRule('documentBusinessRule', pattern),
    ];

    let index = 0;
    let item: Nullable<PatternFormFieldItem>;
    while (!item && index < creators.length) {
      const create = creators[index];
      if (!create) {
        break;
      }

      item = create();
      index += 1;
    }

    return item;
  },
};
